import React, { ReactNode } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

import { Participant, PublicSession } from '@app/api';
import { Trans, useTranslation } from 'react-i18next';

import { getFormatIllustration } from '@app/core';
import { TechnicalRequirements } from './TechnicalRequirements';

const useStyles = createUseStyles((theme: Theme) => ({
  section: {
    [theme.media.mobile]: {
      marginBottom: theme.marginBase * 2,
    },
  },
  subTitle: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 3,
  },
  noBottomMargin: {
    marginBottom: 0,
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.marginBase * 3 + 'px',
  },
  illustration: {
    display: 'block',
    marginBottom: theme.marginBase * 3,
    width: '100%',
    height: 'auto',
    maxHeight: theme.marginBase * 12,
    maxWidth: theme.marginBase * 12,
  },
  illustrationCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.grayBackground,
    paddingTop: theme.marginBase * 4,
    paddingBottom: theme.marginBase * 3,
    width: '100%',
    maxWidth: theme.marginBase * 30,
  },
  title: {
    ...theme.fonts.h5,
  },
  physicalRequirementsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2 + 'px',
  },
  physicalRequirementsTitle: {
    ...theme.fonts.h5,
  },
  physicalRequirementsAdress: {
    ...theme.fonts.caption,
  },
}));

interface Props {
  session: PublicSession;
  title: ReactNode;
  participant?: Participant | undefined;
  titleClassName?: string;
  noBottomMargin?: boolean;
  dynamicDevices?: boolean;
  singleDeviceWarning?: boolean;
}

export const StudyFormatAndRequirements = ({
  session,
  title,
  titleClassName,
  participant,
  noBottomMargin = false,
  dynamicDevices = false,
  singleDeviceWarning = false,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const text = t(`booking.format.${session.format}.title`);

  return (
    <section
      className={classnames(classes.section, {
        [classes.noBottomMargin]: noBottomMargin,
      })}
    >
      <h2 className={classnames(classes.subTitle, titleClassName)}>{title}</h2>
      <div className={classes.content}>
        {session.format && (
          <div className={classes.illustrationCard}>
            <img
              src={getFormatIllustration(session.format)}
              alt={text}
              className={classes.illustration}
            />
            <div className={classes.title} aria-label="Label">
              {text}
            </div>
          </div>
        )}
        {(session.format === 'VIDEO_CALL' || !session.format) && (
          <TechnicalRequirements
            session={session}
            dynamicDevices={dynamicDevices}
            singleDeviceWarning={singleDeviceWarning}
          />
        )}
        {session.format === 'PHYSICAL_MEETING' && (
          <div className={classes.physicalRequirementsContainer}>
            <span className={classes.physicalRequirementsTitle}>
              {t('booking.physicalRequirements.physicalMeetingTitle')}
            </span>
            <span className={classes.physicalRequirementsAdress}>
              {session.address}
            </span>
          </div>
        )}
        {session.format === 'PHONE_CALL' && (
          <div className={classes.physicalRequirementsContainer}>
            <span className={classes.physicalRequirementsTitle}>
              {t('booking.phoneRequirements.phoneMeetingTitle', {
                context: participant?.phoneNumber ? '' : 'annon',
              })}
            </span>
            {participant?.phoneNumber && (
              <span className={classes.physicalRequirementsAdress}>
                {participant.phoneNumber}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
